import _SuperDocESign, {
  SubmitData,
  SigningState,
  SuperDocESignProps,
  SuperDocESignHandle,
  FieldComponentProps,
  SubmitButtonProps,
} from "@superdoc-dev/esign";
import "superdoc/style.css";
import {
  useState,
  useCallback,
  ComponentType,
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  useMemo,
  useEffect,
  useRef,
} from "react";
import { Trans } from "react-i18next";
import { css } from "@emotion/css";

import { useESign, ESignDocumentData } from "./useESign";
import { ContractPdfScroll } from "./ContractPdfScroll";
import { downloadPdfFromResponse } from "./downloadPdfFromResponse";
import { isPdfEsignDocument } from "./isPdfEsignDocument";
import { Link } from "../../components/link/Link";
import { Stack } from "../../foundations/grid-layout/Stack";
import { Text } from "../../foundations/typography/Text";
import { Checkbox } from "../../components/inputs/checkbox/Checkbox";
import { TextField } from "../../components/inputs/text-field/TextField";
import { Signature } from "../../components/signature/Signature";
import { GhostIconButton } from "../../components/button/GhostIconButton";
import { useColors, useScheme } from "../../hooks/useColors";
import { Loader } from "../../components/loaders/loading/Loader";
import { FormFeedback } from "../../components/inputs/_assets/input-wrapper/FormFeedback";

export const documentAriaObserverOptions: MutationObserverInit = {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ["aria-multiline", "role"],
};

/**
 * SuperDoc's ProseMirrorRenderer sets role="document" and aria-multiline together.
 * When both are present, keep document semantics (read/navigate) and drop aria-multiline
 * so assistive tech is not told this is an editable multiline text field.
 */
export function stripProhibitedDocumentAria(root: ParentNode) {
  root.querySelectorAll('[role="document"][aria-multiline]').forEach((node) => {
    node.removeAttribute("aria-multiline");
  });

  if (!(root instanceof Element)) {
    return;
  }

  root.querySelectorAll("*").forEach((element) => {
    if (!element.shadowRoot) {
      return;
    }
    element.shadowRoot
      .querySelectorAll('[role="document"][aria-multiline]')
      .forEach((node) => {
        node.removeAttribute("aria-multiline");
      });
  });
}

const documentRoleFixStyle = css({ display: "contents" });

export function observeDocumentAriaTargets(
  observer: MutationObserver,
  root: ParentNode,
  observedShadowRoots: WeakSet<ShadowRoot> = new WeakSet()
) {
  const visit = (node: ParentNode) => {
    stripProhibitedDocumentAria(node);

    if (!(node instanceof Element || node instanceof DocumentFragment)) {
      return;
    }

    node.querySelectorAll("*").forEach((element) => {
      const shadow = element.shadowRoot;
      if (!shadow || observedShadowRoots.has(shadow)) {
        return;
      }

      observedShadowRoots.add(shadow);
      observer.observe(shadow, documentAriaObserverOptions);
      visit(shadow);
    });
  };

  visit(root);
}

function DocumentRoleFix(props: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) {
      return;
    }

    const observedShadowRoots = new WeakSet<ShadowRoot>();
    const observer = new MutationObserver(() => {
      observeDocumentAriaTargets(observer, root, observedShadowRoots);
    });

    observer.observe(root, documentAriaObserverOptions);
    observeDocumentAriaTargets(observer, root, observedShadowRoots);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={documentRoleFixStyle}>
      {props.children}
    </div>
  );
}

function DownloadLink({
  onClick,
  isLoading,
}: {
  onClick: () => void;
  isLoading?: boolean;
}) {
  return (
    <Link
      testID="esign-download-link"
      onPress={isLoading ? undefined : onClick}
      textColor="text-weak"
      textStyle="global/body-secondary"
      style={{
        opacity: isLoading ? 0.5 : 1,
        cursor: isLoading ? "wait" : "pointer",
      }}
    >
      {isLoading ? <Trans>Downloading...</Trans> : <Trans>Download</Trans>}
    </Link>
  );
}

function ExpandLink({
  onClick,
  isExpanded,
}: {
  onClick: () => void;
  isExpanded: boolean;
}) {
  return (
    <Link
      testID="esign-expand-link"
      onPress={onClick}
      textColor="text-weak"
      textStyle="global/body-secondary"
      rightIcon={isExpanded ? "close" : "open_in_new"}
    >
      {isExpanded ? <Trans>Close</Trans> : <Trans>Expand</Trans>}
    </Link>
  );
}

export interface SubmitControlProps {
  submit: () => void;
  isValid: boolean;
  isScrolled: boolean;
  isSubmitting: boolean;
  isAgreed: boolean;
  hasFields: boolean;
}

export interface ESignSubmitControlProps extends SubmitControlProps {
  /**
   * Custom label for the checkbox. If not provided, defaults to "I understand and agree to this document"
   */
  label?: React.ReactNode;
}

export function ESignSubmitControl({
  submit,
  isAgreed,
  isValid,
  isScrolled,
  isSubmitting,
  hasFields,
  label,
}: ESignSubmitControlProps) {
  const styles = useStyles();
  const defaultLabel = <Trans>I understand and agree to this document</Trans>;
  const checkboxLabel = label ?? defaultLabel;

  if (isAgreed) {
    return (
      <div className={styles.submitCheckboxWrapper}>
        <Checkbox
          isSelected={true}
          isDisabled={true}
          testID="esign-checkbox-submit-agreed"
        >
          {checkboxLabel}
        </Checkbox>
      </div>
    );
  }

  if (!isScrolled) {
    return (
      <div className={styles.submitMessage}>
        <Text testID="please-scroll-text" align="center">
          <Trans>Please scroll to the bottom to accept</Trans>
        </Text>
      </div>
    );
  }

  if (!isValid && hasFields) {
    return (
      <div className={styles.submitMessage}>
        <Text testID="please-fill-fields-text" align="center">
          <Trans>Please fill the fields above</Trans>
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.submitCheckboxWrapper}>
      <Checkbox
        isSelected={false}
        onChange={submit}
        isDisabled={!isValid || isSubmitting}
        testID="esign-submit-checkbox"
      >
        {checkboxLabel}
      </Checkbox>
    </div>
  );
}

function esignAcceptLabel(templateType?: string | null) {
  if (templateType === "DISCLOSURE") {
    return (
      <Trans>
        I acknowledge that I have received, read and understood this statement
      </Trans>
    );
  }
  if (templateType === "CONSENT") {
    return <Trans>I have read, understood, and agree to the above</Trans>;
  }
  return undefined;
}

function DefaultSubmitControl({
  templateType,
  ...props
}: SubmitControlProps & { templateType?: string | null }) {
  return (
    <ESignSubmitControl {...props} label={esignAcceptLabel(templateType)} />
  );
}

const ZOOM_PRESETS = [25, 50, 60, 75, 100, 125, 150, 200] as const;

// TODO: React@19: Can remove, Type assertion to work around React types version mismatch with forwardRef
const SuperDocESign = _SuperDocESign as ForwardRefExoticComponent<
  SuperDocESignProps & RefAttributes<SuperDocESignHandle>
>;

/**
 * Report returned when the API-provided fields don't match the content controls
 * discovered by SuperDoc in the docx. Consumers should forward this to Sentry
 * (or other error tracking) so template/API sync issues are surfaced.
 */
export interface FieldMismatchReport {
  /** Field IDs the API provided that don't exist as content controls in the docx */
  notInDocument: string[];
  /** Content control IDs found in the docx that the API didn't provide values for */
  notInApi: string[];
}

/** Capture settings for SuperDoc signature fields (dark ink on white export surface). */
export const SUPERDOC_SIGNATURE_CAPTURE_SETTINGS = {
  penColor: "#000",
  opaqueWhiteCaptureSurface: true,
} as const;

/**
 * Reports a field mismatch to Sentry (if available in the host application)
 * or falls back to console.error. This is called internally by ESignDocument
 * when the API-provided fields don't match the content controls in the docx.
 */
function reportFieldMismatch(report: FieldMismatchReport, documentId: string) {
  const extra = { ...report, documentId };
  try {
    const { captureMessage } = require("@sentry/browser") as {
      captureMessage: (
        message: string,
        context: { level: string; extra: Record<string, unknown> }
      ) => void;
    };
    captureMessage("ESign field mismatch between API and document", {
      level: "error",
      extra,
    });
  } catch {
    // Sentry not available in host application; log to console for visibility
    console.error("ESign field mismatch between API and document", extra);
  }
}

/**
 * Compares the field IDs provided by the API against the content controls
 * SuperDoc discovered in the docx. Returns a {@link FieldMismatchReport} if
 * there are any mismatches, or `null` if everything lines up.
 */
export function getFieldMismatch(
  discoveredFields: Array<{ id: string | number }>,
  providedFields: Array<{ id?: string; group?: string }>
): FieldMismatchReport | null {
  const discoveredIds = new Set(discoveredFields.map((f) => String(f.id)));
  const providedIds = providedFields
    .map((f) =>
      f.id != null ? String(f.id) : f.group != null ? String(f.group) : null
    )
    .filter((id): id is string => id != null);

  const notInDocument = providedIds.filter((id) => !discoveredIds.has(id));
  const notInApi = [...discoveredIds].filter((id) => !providedIds.includes(id));

  if (notInDocument.length === 0 && notInApi.length === 0) return null;

  return { notInDocument, notInApi };
}

// Helper component for syncing hidden signature fields
// Extracted to satisfy React hooks rules (hooks must be called in components starting with uppercase)
function HiddenSignatureSyncer({
  fieldId,
  onChange,
  sharedSignatureValue,
  registerCallback,
  unregisterCallback,
}: {
  fieldId: string;
  onChange: (value: string | null) => void;
  sharedSignatureValue: string | null;
  registerCallback: (id: string, cb: (value: string | null) => void) => void;
  unregisterCallback: (id: string) => void;
}) {
  // Ref so the registered callback always delegates to the latest onChange
  // without re-triggering the effect (SuperDoc creates a new reference each render).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const stableOnChange = (value: string | null) => onChangeRef.current(value);
    registerCallback(fieldId, stableOnChange);
    if (sharedSignatureValue) {
      stableOnChange(sharedSignatureValue);
    }
    return () => {
      unregisterCallback(fieldId);
    };
    // onChange is accessed via ref; sharedSignatureValue updates are pushed by
    // the parent's sync effect through hiddenSignatureCallbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldId, registerCallback, unregisterCallback]);
  return null;
}

export function ESignDocument({
  data: dataProp,
  onSubmit,
  onDownload,
  downloadFilename,
  submitControl: CustomSubmitControl,
  errorMessage,
  style,
  layoutMode: layoutModeProp,
  allowExpand: allowExpandProp,
}: {
  data?: ESignDocumentData;
  onSubmit?: (data: SubmitData) => void | Promise<void>;
  /**
   * Called when download is requested. Should return a Response object with the PDF blob.
   * ESignDocument handles the blob download and loading state internally.
   */
  onDownload?: () => Promise<Response>;
  /**
   * Custom filename for the downloaded file. If provided, this will be used instead of
   * the filename from the Content-Disposition header. A .pdf extension will be appended
   * if not already present.
   */
  downloadFilename?: string;
  submitControl?: ComponentType<SubmitControlProps>;
  errorMessage?: string;
  style?: React.CSSProperties;
  /**
   * Layout mode for the document. If not provided, will be determined by the
   * requiresPageLayout field from the document data (default: responsive).
   *
   * - "responsive" → web layout (content reflows to fit container, mobile/accessibility)
   * - "paginated" → print layout (fixed page width, displays as it prints)
   */
  layoutMode?: "paginated" | "responsive";
  /**
   * Allow the document to be expanded to fullscreen. If not provided, will be
   * automatically enabled when requiresPageLayout is true.
   */
  allowExpand?: boolean;
}) {
  const [isAgreed, setIsAgreed] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [zoom, setZoom] = useState(60);
  const esignRef = useRef<SuperDocESignHandle>(null);
  const [signingState, setSigningState] = useState<SigningState>({
    scrolled: false,
    fields: new Map(),
    isValid: false,
    isSubmitting: false,
  });

  // Handle download with loading state and blob handling
  const handleDownloadClick = useCallback(async () => {
    if (!onDownload || isDownloading) return;

    setIsDownloading(true);
    setDownloadError(false);
    try {
      const response = await onDownload();

      await downloadPdfFromResponse(response, downloadFilename);
    } catch (e) {
      console.error("Error downloading document", e);
      setDownloadError(true);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, isDownloading, downloadFilename]);

  const isReadOnly = !onSubmit;

  const {
    url,
    documentFields,
    signerFields: apiSignerFields,
    requiresPageLayout,
    signedAt,
    templateType,
    handleSubmit,
  } = useESign({
    data: dataProp,
    onSubmit,
  });

  // Determine effective layout mode:
  // 1. Use explicit prop if provided
  // 2. Otherwise, use "paginated" if requiresPageLayout is true
  // 3. Default to "responsive"
  const layoutMode: "paginated" | "responsive" =
    layoutModeProp ?? (requiresPageLayout ? "paginated" : "responsive");

  // Whether the user previously completed signing (from API)
  const wasPreviouslySigned = Boolean(signedAt);

  // Determine if expand is allowed:
  // 1. Use explicit prop if provided
  // 2. Otherwise, enable for paginated layout (requiresPageLayout)
  const allowExpand = allowExpandProp ?? requiresPageLayout;

  const scheme = useScheme();
  const isDarkMode = scheme === "dark";
  const styles = useStyles(layoutMode, isExpanded, isDarkMode);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Handle Escape key to close expanded view
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsExpanded(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isExpanded]);

  // Zoom control for paginated (print) layout:
  // - Default: 60% so the full page width fits comfortably in the compact container
  // - Expanded: 100% so the document renders at natural size in the fullscreen modal
  useEffect(() => {
    if (layoutMode !== "paginated") return;
    esignRef.current?.setZoom(isExpanded ? 100 : 60);
  }, [isExpanded, layoutMode]);

  // Track shared signature value to sync across all signature fields.
  // - undefined = not yet initialized (fall back to SuperDoc's FieldComponentProps.value)
  // - null = explicitly cleared by the user
  // - string = drawn/restored signature data URL
  const [sharedSignatureValue, setSharedSignatureValue] = useState<
    string | null | undefined
  >(undefined);

  // Ref that always holds the latest signature value so closures inside the
  // signerFields useMemo can read the current value without being recreated
  // on every stroke (which would remount the Signature canvas and clear it).
  const sharedSignatureValueRef = useRef<string | null | undefined>(undefined);
  sharedSignatureValueRef.current = sharedSignatureValue;

  // When API data loads with a previous signing, initialize agreed state and
  // restore pre-existing signer field values (e.g. signature from a previous session)
  const hasInitializedFromApi = useRef(false);
  useEffect(() => {
    if (hasInitializedFromApi.current || apiSignerFields.length === 0) return;
    hasInitializedFromApi.current = true;

    // If the user previously signed, mark as agreed immediately
    if (wasPreviouslySigned) {
      setIsAgreed(true);
    }

    // Restore the signature value from the first signature field that has one
    const existingSignatureField = apiSignerFields.find(
      (field) => field.type === "signature" && field.value
    );
    if (existingSignatureField?.value) {
      setSharedSignatureValue(existingSignatureField.value as string);
    }
  }, [apiSignerFields, wasPreviouslySigned]);

  // Track onChange callbacks for hidden signature fields to sync values
  const hiddenSignatureCallbacks = useRef<
    Map<string, (value: string | null) => void>
  >(new Map());

  // Sync signature value to all hidden signature fields when it changes
  useEffect(() => {
    // Skip sync when not yet initialized (undefined)
    if (sharedSignatureValue === undefined) return;
    hiddenSignatureCallbacks.current.forEach((onChange) => {
      onChange(sharedSignatureValue);
    });
  }, [sharedSignatureValue]);

  // Collect all signature field IDs for syncing
  const signatureFieldIds = useMemo(() => {
    return apiSignerFields
      .filter((field) => field.type === "signature")
      .map((field) => field.id);
  }, [apiSignerFields]);

  // Callbacks for registering/unregistering hidden signature field sync
  const registerSignatureCallback = useCallback(
    (id: string, cb: (value: string | null) => void) => {
      hiddenSignatureCallbacks.current.set(id, cb);
    },
    []
  );

  const unregisterSignatureCallback = useCallback((id: string) => {
    hiddenSignatureCallbacks.current.delete(id);
  }, []);

  // Add custom components to API signer fields
  const signerFields = useMemo(() => {
    let firstSignatureFieldSeen = false;

    return apiSignerFields.map((field) => {
      if (field.type === "signature") {
        const isFirstSignature = !firstSignatureFieldSeen;
        firstSignatureFieldSeen = true;

        // Only render the Signature component for the first signature field
        // Other signature fields will use the shared value automatically
        if (isFirstSignature) {
          return {
            ...field,
            component: ({
              value,
              onChange,
              isDisabled,
            }: FieldComponentProps) => {
              // Handle signature change - update shared state and propagate to all signature fields
              const handleSignatureChange = (newValue: string | null) => {
                setSharedSignatureValue(newValue);
                onChange(newValue);
              };

              // Determine signature value:
              // - If our shared ref has been explicitly set (string or null), use it
              // - If undefined (not yet initialized), fall back to SuperDoc's
              //   FieldComponentProps.value (the initial value from the signer field definition)
              const signatureValue =
                sharedSignatureValueRef.current !== undefined
                  ? sharedSignatureValueRef.current
                  : (value as string | null);

              return (
                <Signature
                  onChange={handleSignatureChange}
                  onClear={() => handleSignatureChange(null)}
                  disabled={isDisabled}
                  height={120}
                  minWidth={280}
                  penColor={SUPERDOC_SIGNATURE_CAPTURE_SETTINGS.penColor}
                  opaqueWhiteCaptureSurface={
                    SUPERDOC_SIGNATURE_CAPTURE_SETTINGS.opaqueWhiteCaptureSurface
                  }
                  value={signatureValue}
                />
              );
            },
          };
        }

        // For subsequent signature fields, render a hidden component that syncs its value
        // with the first signature field via the HiddenSignatureSyncer component
        const fieldId = field.id;
        return {
          ...field,
          component: ({ onChange }: FieldComponentProps) => (
            <HiddenSignatureSyncer
              fieldId={fieldId}
              onChange={onChange}
              sharedSignatureValue={sharedSignatureValue ?? null}
              registerCallback={registerSignatureCallback}
              unregisterCallback={unregisterSignatureCallback}
            />
          ),
        };
      }
      if (field.type === "checkbox") {
        return {
          ...field,
          component: ({
            value,
            onChange,
            isDisabled,
            label,
          }: FieldComponentProps) => (
            <Checkbox
              isSelected={Boolean(value)}
              onChange={onChange}
              isDisabled={isDisabled}
              testID={`esign-checkbox-field-${field.id}`}
            >
              {label}
            </Checkbox>
          ),
        };
      }
      return {
        ...field,
        component: ({
          value,
          onChange,
          isDisabled,
          label,
        }: FieldComponentProps) => (
          <TextField
            testID="esign-text-field"
            label={label}
            value={String(value ?? "")}
            onChange={onChange}
            isDisabled={isDisabled}
          />
        ),
      };
    });
    // NOTE: sharedSignatureValue intentionally NOT included as a dependency here.
    // Including it would cause the component functions to be recreated when the signature
    // changes, which would remount the Signature component and clear the canvas.
    // Hidden signature fields receive synced values via the useEffect that calls
    // hiddenSignatureCallbacks.current.forEach() when sharedSignatureValue changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiSignerFields, registerSignatureCallback, unregisterSignatureCallback]);

  const handleStateChange = useCallback((state: SigningState) => {
    // Only update if values we care about actually changed to avoid unnecessary re-renders
    setSigningState((prev) => {
      if (
        prev.scrolled === state.scrolled &&
        prev.isValid === state.isValid &&
        prev.isSubmitting === state.isSubmitting
      ) {
        return prev; // Same reference → no re-render
      }
      return state;
    });
  }, []);

  // Wrapper to merge SuperDoc's props with our Custom Control props
  const SubmitControlWrapper = useCallback(
    (props: SubmitButtonProps) => {
      const baseProps: SubmitControlProps = {
        submit: props.onClick,
        isValid: signingState.isValid,
        isScrolled: signingState.scrolled,
        isSubmitting: signingState.isSubmitting,
        isAgreed,
        hasFields: signerFields.length > 0,
      };

      if (CustomSubmitControl) {
        return <CustomSubmitControl {...baseProps} />;
      }

      return (
        <DefaultSubmitControl {...baseProps} templateType={templateType} />
      );
    },
    [
      CustomSubmitControl,
      signingState.isValid,
      signingState.scrolled,
      signingState.isSubmitting,
      isAgreed,
      signerFields.length,
      templateType,
    ]
  );

  // Toolbar component that includes zoom controls, download and expand links
  // NOTE: This must be defined before early returns to maintain consistent hook order
  const ToolbarComponent = useCallback(
    () => (
      <div className={styles.toolbarLinks}>
        {layoutMode === "paginated" && (
          <Stack orientation="horizontal" align="center" gap="space-1">
            <GhostIconButton
              icon="remove"
              size="tiny"
              onPress={() => {
                const prev = ZOOM_PRESETS.filter((z) => z < zoom);
                if (prev.length > 0)
                  esignRef.current?.setZoom(prev[prev.length - 1]);
              }}
              isDisabled={zoom <= ZOOM_PRESETS[0]}
              aria-label="Zoom out"
              testID="esign-zoom-out"
            />
            <Text
              style="global/body-secondary"
              color="text-weak"
              align="center"
            >
              {zoom}%
            </Text>
            <GhostIconButton
              icon="add"
              size="tiny"
              onPress={() => {
                const next = ZOOM_PRESETS.find((z) => z > zoom);
                if (next) esignRef.current?.setZoom(next);
              }}
              isDisabled={zoom >= ZOOM_PRESETS[ZOOM_PRESETS.length - 1]}
              aria-label="Zoom in"
              testID="esign-zoom-in"
            />
          </Stack>
        )}
        {allowExpand && (
          <ExpandLink onClick={handleExpandToggle} isExpanded={isExpanded} />
        )}
        <DownloadLink onClick={handleDownloadClick} isLoading={isDownloading} />
        {downloadError && (
          <Text color="text-danger" testID="esign-download-error">
            <Trans>Failed to download document</Trans>
          </Text>
        )}
      </div>
    ),
    [
      layoutMode,
      zoom,
      esignRef,
      allowExpand,
      handleExpandToggle,
      isExpanded,
      handleDownloadClick,
      isDownloading,
      downloadError,
      styles.toolbarLinks,
    ]
  );

  // Merge pre-filled signer field values into document fields so SuperDoc
  // renders them in the document body (e.g. signature images). Signer field
  // `value` only pre-fills the interactive control; adding to `fields.document`
  // causes SuperDoc to inject the value into the matching document annotation.
  // NOTE: Must be before early returns to maintain consistent hook order.
  const mergedDocumentFields = useMemo(() => {
    const prefilledSignerFields = apiSignerFields
      .filter((field) => field.value != null)
      .map((field) => ({
        id: field.id,
        value: field.value as string,
      }));

    if (prefilledSignerFields.length === 0) return documentFields;
    return [...documentFields, ...prefilledSignerFields];
  }, [documentFields, apiSignerFields]);

  // Stable eventId per document — must NOT change across re-renders
  // or SuperDoc will re-initialize the signing session on each render.
  const eventId = useMemo(
    () => `esign-${url ?? "unknown"}-${Date.now()}`,
    [url]
  );

  // Loading state — data not yet available from the consumer
  if (!dataProp) {
    return (
      <section className={styles.section}>
        <header className={styles.placeholderHeader}>
          {/* Placeholder for download link */}
        </header>
        <div className={styles.placeholderContent}>
          <Stack
            gap="space-4"
            align="center"
            justify="center"
            style={{ height: "100%" }}
          >
            <Loader type="dots" />
            <Text>
              <Trans>Loading document...</Trans>
            </Text>
          </Stack>
        </div>
        <footer className={styles.placeholderFooter} />
      </section>
    );
  }

  // PDF: Use ContractPdfScroll for PDF documents since SuperDoc can't properly load PDFs.
  // Check this before the error/empty-URL check because PDFs don't use the SuperDoc URL.
  // Omit onSubmit when the parent is read-only so the preview does not show an agree checkbox.
  if (isPdfEsignDocument(dataProp)) {
    return (
      <ContractPdfScroll
        url={url ?? ""}
        onDownload={onDownload}
        downloadFilename={downloadFilename}
        onSubmit={
          onSubmit
            ? async () => {
                await onSubmit({
                  eventId: "",
                  timestamp: new Date().toISOString(),
                  duration: 0,
                  auditTrail: [],
                  documentFields: [],
                  signerFields: [],
                  isFullyCompleted: true,
                });
              }
            : undefined
        }
        errorMessage={errorMessage}
        label={esignAcceptLabel(templateType)}
        style={style}
      />
    );
  }

  // Error state — data provided but URL missing
  if (!url) {
    return (
      <section className={styles.section}>
        <header className={styles.placeholderHeader}>
          {/* Placeholder for download link */}
        </header>
        <div className={styles.placeholderContent}>
          <Stack
            gap="space-4"
            align="center"
            justify="center"
            style={{ height: "100%" }}
          >
            <Text color="text-danger">
              <Trans>Failed to load document</Trans>
            </Text>
          </Stack>
        </div>
        <footer className={styles.placeholderFooter} />
      </section>
    );
  }

  const documentContent = (
    <DocumentRoleFix>
      <SuperDocESign
        ref={esignRef}
        eventId={eventId}
        document={{
          source: url,
          mode: isReadOnly ? "download" : "full",
          viewOptions: {
            layout: layoutMode === "responsive" ? "web" : "print",
          },
          ...(!isReadOnly && {
            validation: {
              scroll: { required: !wasPreviouslySigned },
            },
          }),
        }}
        fields={{
          document: mergedDocumentFields,
          signer: isReadOnly ? [] : signerFields,
        }}
        documentHeight="100%"
        download={{
          component: ToolbarComponent,
        }}
        {...(!isReadOnly && {
          submit: {
            component: SubmitControlWrapper,
          },
        })}
        onSubmit={async (data: SubmitData) => {
          if (isReadOnly) return;

          // Sync signature value to all signature fields before submitting
          if (sharedSignatureValue && signatureFieldIds.length > 1) {
            const syncedSignerFields = [...data.signerFields];
            signatureFieldIds.forEach((fieldId) => {
              const existingIndex = syncedSignerFields.findIndex(
                (f) => f.id === fieldId
              );
              if (existingIndex >= 0) {
                syncedSignerFields[existingIndex] = {
                  ...syncedSignerFields[existingIndex],
                  value: sharedSignatureValue,
                };
              } else {
                syncedSignerFields.push({
                  id: fieldId,
                  value: sharedSignatureValue,
                });
              }
            });
            data = { ...data, signerFields: syncedSignerFields };
          }

          // Enrich signer fields with `type` from the field definitions so
          // consumers don't need to pass the original fields separately.
          const enrichedSignerFields = data.signerFields.map((field) => {
            const original = apiSignerFields.find((f) => f.id === field.id);
            return { ...field, type: original?.type ?? "signature" };
          });

          // Only lock the accept checkbox after a resolved submit. Callers must
          // rethrow (useESignSubmit throwErrors) so a 400 does not set isAgreed.
          await handleSubmit({ ...data, signerFields: enrichedSignerFields });
          setIsAgreed(true);
        }}
        onFieldsDiscovered={(fields) => {
          if (layoutMode === "paginated") {
            esignRef.current?.setZoom(isExpanded ? 100 : 60);
          }

          const allProvidedFields = [
            ...mergedDocumentFields,
            ...apiSignerFields.map((f) => ({ id: f.id })),
          ];
          const mismatch = getFieldMismatch(fields, allProvidedFields);
          if (mismatch) {
            reportFieldMismatch(mismatch, url ?? "unknown");
          }
        }}
        isDisabled={isReadOnly || isAgreed}
        onStateChange={isReadOnly ? undefined : handleStateChange}
        onZoomChange={({ zoom }) => setZoom(zoom)}
        className={styles.superDocContainer}
        telemetry={{ enabled: false }}
      />
    </DocumentRoleFix>
  );

  // Expanded/fullscreen view
  if (isExpanded) {
    return (
      <>
        <div
          className={styles.expandedOverlay}
          onClick={(e) => {
            // Close when clicking on the overlay (not the container)
            if (e.target === e.currentTarget) {
              setIsExpanded(false);
            }
          }}
        >
          <div className={styles.expandedContainer}>{documentContent}</div>
        </div>
        {/* Keep original position element to maintain layout */}
        <div className={styles.container} style={style}>
          <section className={styles.section}>
            <div className={styles.placeholderContent}>
              <Stack
                gap="space-4"
                align="center"
                justify="center"
                style={{ height: "100%" }}
              >
                <Text>
                  <Trans>Document expanded to fullscreen</Trans>
                </Text>
                <Link testID="esign-collapse-link" onPress={handleExpandToggle}>
                  <Trans>Click to collapse</Trans>
                </Link>
              </Stack>
            </div>
          </section>
        </div>
      </>
    );
  }

  return (
    <div className={styles.container} style={style}>
      <section
        className={css(styles.section, {
          borderColor: errorMessage ? "var(--border-danger-input)" : undefined,
        })}
      >
        {documentContent}
      </section>
      {errorMessage && (
        <FormFeedback type="invalid">{errorMessage}</FormFeedback>
      )}
    </div>
  );
}

function useStyles(
  layoutMode?: "paginated" | "responsive",
  isExpanded?: boolean,
  isDarkMode?: boolean
) {
  const colors = useColors();
  const isResponsive = layoutMode === "responsive";

  return {
    container: css({
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }),
    section: css({
      backgroundColor: "var(--background-base)",
      // In responsive mode: fill container width
      // In paginated mode: size to fit SuperDoc's natural page width
      width: isResponsive ? "100%" : "fit-content",
      maxWidth: "100%",
      minHeight: "322px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      border: `1px solid ${colors["border-base"]}`,
      borderRadius: "var(--border-radius-3)",
      overflow: "hidden", // Clip content to border radius
    }),
    toolbarLinks: css({
      display: "flex",
      alignItems: "center",
      gap: "var(--space-4)",
    }),
    // Expanded/fullscreen styles
    expandedOverlay: css({
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "var(--space-6)",
    }),
    expandedContainer: css({
      width: "100%",
      // 864px = 816px default page width + 48px margin (24px each side)
      maxWidth: "864px",
      height: "100%",
      maxHeight: "calc(100vh - var(--space-12))",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "var(--background-base)",
      borderRadius: "var(--border-radius-4)",
      overflow: "hidden",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    }),
    superDocContainer: css({
      height: "100%",
      width: "100%",
      maxWidth: "100%",
      display: "flex",
      flexDirection: "column",

      // Root container - ensure flex layout
      "&.superdoc-esign-container": {
        display: "flex",
        flexDirection: "column",
      },

      // Toolbar (Header) Styles
      "& .superdoc-esign-document-toolbar": {
        display: "flex",
        justifyContent: "flex-end",
        padding: "var(--space-3) var(--space-4)",
        borderBottom: `1px solid ${colors["border-base"]}`,
        backgroundColor: "var(--background-base)",
        flexShrink: 0,
      },

      // Document section wrapper
      "& .superdoc-esign-document": {
        border: "none",
        borderRadius: 0,
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      },

      // Document viewer (scroll container where SuperDoc mounts)
      "& .superdoc-esign-document-viewer": {
        margin: 0,
        flex: 1,
        height: "auto !important",
        overflow: "auto",
        // Content borders
        borderTop: `1px solid ${colors["border-base"]}`,
        borderBottom: `1px solid ${colors["border-base"]}`,
        // Layout-specific background
        ...(isResponsive
          ? {
              // Web layout: clean white background
              backgroundColor: colors["background-base"],
            }
          : {
              // Print layout: sunken "desk" background with page styling
              backgroundColor: colors["surface-sunken"],
              padding: "var(--space-4) var(--space-6)",
              boxSizing: "content-box",
              // On narrow screens, remove desk padding
              "@media (max-width: 560px)": {
                backgroundColor: colors["background-base"],
                padding: 0,
              },
            }),
        // Document text color
        "& *": {
          color: `var(--text-weak) !important`,
        },
      },

      // Controls (Footer) area - fields and actions
      "& .superdoc-esign-controls": {
        padding: "var(--space-3) var(--space-4)",
        backgroundColor: "var(--background-base)",
        borderTop: `1px solid ${colors["border-base"]}`,
        marginTop: "0 !important",
        borderRadius: "0 0 var(--border-radius-3) var(--border-radius-3)",
        flexShrink: 0,
      },

      // Field container
      "& .superdoc-esign-fields": {
        marginBottom: "var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
        alignItems: "center",
        "&:empty": {
          display: "none",
        },
      },

      // Form submit button container
      "& .superdoc-esign-form-actions": {
        width: "100%",
        justifyContent: "center",
      },

      // Print layout (paginated) - "page on desk" appearance
      ...(!isResponsive && {
        // Parent containers fill width so margin auto can work
        "& .superdoc": {
          width: "100% !important",
        },
        "& .superdoc__layers": {
          width: "100% !important",
        },
        "& .superdoc__document": {
          width: "100% !important",
        },
        "& .superdoc__sub-document": {
          width: "100% !important",
        },
        // Use margin auto for centering - this automatically:
        // - Centers when container is wider than content
        // - Left-aligns when container is narrower (allows scroll right)
        "& .super-editor-container": {
          margin: "0 auto",
        },
        "& .super-editor": {
          minHeight: "100%",
        },
      }),

      // Web layout (responsive) - SuperDoc handles reflow via viewOptions.layout='web'
      // Only need minimal overrides for our container integration
      ...(isResponsive && {
        overflow: "hidden",
        "& .super-editor": {
          minHeight: "100%",
        },
        // Add padding to the content area for better readability
        "& .super-editor__element": {
          padding: "var(--space-4) var(--space-5)",
          boxSizing: "border-box",
        },
        // Dark mode: override SuperDoc's inline white backgrounds on the
        // exact elements that receive them (inline styles require !important)
        ...(isDarkMode && {
          // The editor element has inline style="background-color: rgb(255, 255, 255)"
          "& .super-editor__element": {
            padding: "var(--space-4) var(--space-5)",
            boxSizing: "border-box",
            backgroundColor: `${colors["background-base"]} !important`,
          },
          // ProseMirror also gets inline background-color: rgb(255, 255, 255)
          "& .ProseMirror": {
            backgroundColor: `${colors["background-base"]} !important`,
          },
        }),
      }),
    }),
    submitMessage: css({
      width: "100%",
      textAlign: "center",
    }),
    submitCheckboxWrapper: css({
      width: "100%",
      display: "flex",
      justifyContent: "flex-start",
    }),
    placeholderHeader: css({
      height: "57px", // Approximate height of the toolbar
      borderBottom: `1px solid ${colors["border-base"]}`,
      backgroundColor: "var(--background-base)",
    }),
    placeholderContent: css({
      flex: 1,
      backgroundColor: colors["surface-sunken"],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    placeholderFooter: css({
      height: "65px", // Approximate height of the footer
      borderTop: `1px solid ${colors["border-base"]}`,
      backgroundColor: "var(--background-base)",
    }),
  };
}
